"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { ArrowRight, Globe2, MapPin, Plane } from "lucide-react";

export type RegionalPreviewCard = {
  kind: "country" | "regional" | "global";
  title: string;
  subtitle: string;
  price?: string;
  href: string;
};

const ICONS = {
  country: MapPin,
  regional: Plane,
  global: Globe2,
} as const;

/**
 * SkyLane — Regional / global plan preview (DESIGN_BRIEF §4 homepage order:
 * between BoardingSteps and the compatibility teaser). Crisp white cards, solid
 * sky-blue accents (no glass/gradient blobs — that is the AquaGlobe archetype).
 * Real catalog plans + checkout hrefs are passed in from the server; this client
 * component only handles presentation. Claim-safe (no price superlatives).
 */
export default function RegionalPreview({ cards }: { cards: RegionalPreviewCard[] }) {
  const { t } = useTranslations();

  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="div" y={20} className="rounded-[28px] border border-border bg-card p-6 sm:p-8">
        <div className="max-w-2xl">
          <span className="inline-flex rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] text-primary">
            {t("sky.regional.eyebrow", "Going beyond one stop?")}
          </span>
          <h2 className="mt-3 text-2xl font-extrabold text-foreground sm:text-3xl">
            {t("sky.regional.title", "Choose the pass that fits your route")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground sm:text-base">
            {t("sky.regional.sub", "Start with one destination, step up to a regional or global pass when your trip includes connections or multi-country meetings.")}
          </p>
        </div>

        <Stagger as="div" staggerChildren={0.07} className="mt-6 grid gap-4 lg:grid-cols-3">
          {cards.map((card) => {
            const Icon = ICONS[card.kind];
            return (
              <StaggerItem key={`${card.kind}-${card.title}`} as="div" y={12}>
                <Link
                  href={card.href}
                  className="group block h-full rounded-[24px] border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-primary/40 hover:shadow-md"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="mt-4 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-foreground">{card.title}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{card.subtitle}</p>
                    </div>
                    {card.price ? (
                      <span className="shrink-0 rounded-full bg-primary/10 px-3 py-1 text-sm font-bold text-primary">{card.price}</span>
                    ) : null}
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-primary">
                    {t("sky.regional.cta", "View route-ready plans")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </StaggerItem>
            );
          })}
        </Stagger>
      </Reveal>
    </section>
  );
}
